import { z } from 'zod';
import { API_ObjectAttributesSchema } from '../telematics/types-telematics';
import {
    AnalyticsCfgClusterSubMenuSchema,
    FilterSubMenuValueSchema,
} from '../telematics/types-analytics';
import { FilterColumnsSchema, FilterGroupsSchema } from '../telematics/types';
import { AvailableValuesSchema } from '../telematics/components/FilterByParameters/Filter';

export const LastMileConfigResponseSchema = z.object({
    availableTypes: z.array(API_ObjectAttributesSchema),
    availableValues: AvailableValuesSchema,
    subMenus: z.record(z.string(), FilterSubMenuValueSchema), // z.record(z.string(), z.any()),
    filterColumns: FilterColumnsSchema,
    filterGroups: FilterGroupsSchema,
    clusters: z.record(AnalyticsCfgClusterSubMenuSchema).or(z.array(z.any()).length(0)).optional(),
});
export type LastMileConfigResponseType = z.infer<typeof LastMileConfigResponseSchema>;

export const helpAndSupportSchema = z.object({
    email: z.string().catch(''),
    phone: z.string().catch(''),
});
export type HelpAndSupportType = z.infer<typeof helpAndSupportSchema>;

export const helpAndSupportDefaults: HelpAndSupportType = {
    email: 'pro@automapa.pl',
    phone: '+48 22 584 60 00',
};

export type RouteCostFields = {
    per_item: number;
    per_min: number;
    per_km: number;
    per_init: number;
};

export const routeCostDefaults: RouteCostFields & { unit: string, tolls: boolean } = {
    unit: 'zł',
    tolls: false,
    per_item: 0, // -- ustalone z Rafałem telefonicznie 2024.08.22
    per_km: 0.8, // -- ustalone z Rafałem telefonicznie 2024.08.22
    per_min: 0.6, // -- ustalone z Rafałem telefonicznie 2024.08.22
    per_init: 0,
};

export const AVAILABLE_OVERLOAD_ACTIONS = ['accept', 'reject', 'return']; // , 'ignore'
export const vehicleOverloadActionsDefault = {
    weight_limit: AVAILABLE_OVERLOAD_ACTIONS[0],
    parcel_limit: AVAILABLE_OVERLOAD_ACTIONS[0],
    time_window: AVAILABLE_OVERLOAD_ACTIONS[0],
};

export const RouteCostSchema = z.object({
    unit: z.string().catch(routeCostDefaults.unit),
    tolls: z.boolean().catch(routeCostDefaults.tolls),
    per_item: z.coerce.number().catch(routeCostDefaults.per_item),
    per_km: z.coerce.number().catch(routeCostDefaults.per_km),
    per_min: z.coerce.number().catch(routeCostDefaults.per_min),
    per_init: z.coerce.number().catch(routeCostDefaults.per_init ?? 0),
} satisfies { [K in keyof RouteCostFields | 'unit' | 'tolls']: z.ZodTypeAny });

export type RouteCostType = z.infer<typeof RouteCostSchema>;

export const CustomerSettingsSchema = z.object({
    admin: z
        .object({
            show_data_reset_button: z.boolean().optional().catch(true),
            template_data: z.string().optional().catch(''),
            template_id: z.number().optional(),
        })
        .catch({}),
    expiration_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/i)
        .nullable()
        .catch(null),
    is_template: z.boolean().catch(false),
    each_stop_minutes: z.coerce.number().min(0).max(60).catch(1),
    each_delivery_minutes: z.coerce.number().min(0).max(60).catch(1),
    each_warehouse_delivery_minutes: z.coerce.number().min(0).max(60).catch(1),
    send_contents: z.string().catch('Dostarczylismy Twój posiłek. Smacznego!'),
    send_method: z.string().catch('send_method_sms'),
    send_methods: z
        .array(
            z.object({
                id: z.string().default('send_method_no'), //, 'send_method_sms', 'send_method_gate']),
                enabled: z.boolean(),
            })
        )
        .min(1)
        .catch([
            { id: 'send_method_no', enabled: true },
            { id: 'send_method_sms', enabled: true },
            { id: 'send_method_gate', enabled: false },
        ]),
    planning: z
        .object({
            start_time_route: z
                .string()
                .regex(/^\d{2}:\d{2}$/)
                .catch('10:00'),
        })
        .catch({ start_time_route: '10:00' }),
    available_vehicle_options: z
        .array(z.string())
        .default(['route', 'routeend', 'truck', 'palette_limit', 'weight_limit'])
        .catch([]),
    map_key: z.string().catch(''),
    display: z
        .object({
            // icon_style: z.union([z.literal('classic'), z.literal('outline')]).catch('classic'),
            max_point_zoom: z.coerce.number().int().min(20).max(31).catch(26),
        })
        .catch({ max_point_zoom: 26 }),
    help_and_support: z
        .object({
            email: z.string().catch(helpAndSupportDefaults.email),
            phone: z.string().catch(helpAndSupportDefaults.phone),
        })
        .catch(helpAndSupportDefaults),
    route_cost: RouteCostSchema.catch(routeCostDefaults),
    vehicle_overload_actions: z
        .object({
            weight_limit: z.string().default(AVAILABLE_OVERLOAD_ACTIONS[0]),
            parcel_limit: z.string().default(AVAILABLE_OVERLOAD_ACTIONS[0]),
            time_window: z.string().default(AVAILABLE_OVERLOAD_ACTIONS[0]),
        })
        .catch(vehicleOverloadActionsDefault),
    vehicle_overload_checking_when_planning: z
        .object({
            check_weight_limit: z.boolean().default(false),
            check_parcel_limit: z.boolean().default(false),
        })
        .catch({
            check_weight_limit: false,
            check_parcel_limit: false,
        }),
    ignore_last_point_on_optimization: z.boolean().catch(false),
    export: z
        .object({
            realisation_sort_by_zone: z.enum(['original', 'by_zone']).catch('by_zone'),
            delivery_list: z
                .object({
                    included_client_attributes: z.array(z.string()).catch([]),
                    included_special_attributes: z.array(z.string()).catch([]),
                })
                .catch({
                    included_client_attributes: [],
                    included_special_attributes: [],
                }),
        })
        .catch({
            realisation_sort_by_zone: 'by_zone' as 'original' | 'by_zone',
            delivery_list: {
                included_client_attributes: [] as string[],
                included_special_attributes: [] as string[],
            },
        }),
});
export type CustomerSettingsType = z.infer<typeof CustomerSettingsSchema>;

export const defaultClientSettings = {
    locale: 'pl',
    display: {
        showRelativeToday: true,
        icon_style: 'bold',
        max_point_zoom: 26,
        point_cluster_distance: 50,
        selected_job_color_method: 'target_date',
        zoom_map_on_every_change: true,
        simple_view: false,
        readonlyMode: false,
        selectedDay: null,
    },
    // export: {
    //     realisation_sort_by_zone: 'by_zone' as 'original' | 'by_zone',
    //     delivery_list: {
    //         included_client_attributes: [] as string[],
    //         included_special_attributes: [] as string[],
    //     },
    // },
};

export const customerSettingsDefault = CustomerSettingsSchema.parse(defaultClientSettings);

export const ClientSettingsSchema = z
    .object({
        locale: z.string().catch(defaultClientSettings.locale),
        display: z
            .object({
                readonlyMode: z.boolean().catch(false),
                selectedDay: z.string().nullable().catch(null),
                showRelativeToday: z
                    .boolean()
                    .catch(defaultClientSettings.display.showRelativeToday),
                icon_style: z
                    .string()
                    .default(defaultClientSettings.display.icon_style)
                    .catch(defaultClientSettings.display.icon_style),
                point_cluster_distance: z
                    .number()
                    .catch(defaultClientSettings.display.point_cluster_distance),
                selected_job_color_method: z.string().catch('target_date'),
                zoom_map_on_every_change: z.boolean().catch(true),
                simple_view: z.boolean().catch(false).describe('replace store simpleView'),
                // max_point_zoom: z.coerce.number().int().min(20).max(31).catch(26)
            })
            .catch(defaultClientSettings.display),
        // export: z
        //     .object({
        //         realisation_sort_by_zone: z.enum(['original', 'by_zone']).catch('by_zone'),
        //         delivery_list: z
        //             .object({
        //                 included_client_attributes: z.array(z.string()).catch([]),
        //                 included_special_attributes: z.array(z.string()).catch([]),
        //             })
        //             .catch({
        //                 included_client_attributes: [],
        //                 included_special_attributes: [],
        //             }),
        //     })
        //     .catch(defaultClientSettings.export),
    })
    .catch(defaultClientSettings);
export type ClientSettingsType = z.infer<typeof ClientSettingsSchema>;

export const clientSettingsDefault = ClientSettingsSchema.parse({});

export const CustomerSettingsReadonlySchema = z.object({
    max_locations: z.number(),
    max_rows_per_file: z.number(),
    max_jobs_per_route: z.number(),
    enabled_map_layers: z.array(z.string()),
    job_description: z.object({
        label: z.string(),
        text: z.string(),
        subtext: z.string(),
    }),
    enabled_locales: z.array(z.string()),
    compare_scenarios: z.boolean().catch(false),
    enable_job_modify_time_window_rule: z.boolean().catch(false),
    minimum_geocoding_quality: z.number().catch(8),
    mobile_app_options: z.any(),
    master_customer: z
        .object({
            enabled: z.boolean().catch(false),
            available_customer_ids: z.array(z.number()).catch([]),
        })
        .catch({ enabled: false, available_customer_ids: [] }),
    micro_grid: z.boolean().catch(false),
    country: z.union([z.boolean(), z.string()]).catch('pl')
});
export type CustomerSettingsReadonlyType = z.infer<typeof CustomerSettingsReadonlySchema>;
